import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogContent, Divider, FormControl, IconButton, MenuItem, Select,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalText from '../common/LocalText';
import { useToast } from '../../context/ToastContext';
import { getPlayerHistory } from '../../api/gameScoreBoardApi';
import {
  C, AVATAR_PALETTE, btnBase, ctaNavy,
} from '../../theme/adminTokens';
import { colorForId, initialOf } from '../../utils/avatarColor';
import { describeScoreboardRange } from '../../utils/scoreboardRangeLabel';

// Static for now, per explicit direction — not driven by any backend-configurable value
// (the backend's @Limit already accepts anything up to 50, this is just which of those
// values the CMS exposes as quick picks).
const HISTORY_LIMIT_OPTIONS = [5, 10, 15];
const DEFAULT_HISTORY_LIMIT = HISTORY_LIMIT_OPTIONS[0];

const avatarColorFor = (userId) => colorForId(userId, AVATAR_PALETTE);

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const tileSx = {
  flex: 1,
  minWidth: 120,
  borderRadius: 2,
  border: `1px solid ${C.border}`,
  bgcolor: '#f8fafc',
  p: 1.5,
  textAlign: 'center',
};

// A small, hand-rolled inline SVG line chart for up to HISTORY_LIMIT (score, playedAt)
// points — no charting library exists in this project (see UserBreakdownBars.jsx for the
// same hand-rolled-visualization convention, there as bars), and this is only 5 points.
function MatchHistoryChart({ matches }) {
  if (!matches || matches.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: C.muted, textAlign: 'center', py: 3 }}>
        <LocalText>No match history for this period.</LocalText>
      </Typography>
    );
  }

  // Width grows with the point count instead of staying fixed — at 15 points a fixed
  // 320px would crush the score/date labels into an unreadable pile. The dialog's
  // container already scrolls horizontally (see the wrapping Box below), so growing past
  // the visible area is fine rather than something to avoid.
  const minWidth = 320;
  const perPointSpacing = 56;
  const width = Math.max(minWidth, 48 + (matches.length - 1) * perPointSpacing);
  const height = 140;
  // Room for a score label above each point and a date label below the baseline —
  // a bare dot/line with no labels was the original version's real flaw (a single point
  // with nothing printed near it just looks like a rendering glitch, not "1 match, score 6").
  const padTop = 22;
  const padBottom = 28;
  const padSide = 24;
  const plotHeight = height - padTop - padBottom;

  const scores = matches.map((m) => m.score ?? 0);
  const maxScore = Math.max(...scores, 1);
  const minScore = Math.min(...scores, 0);
  const range = maxScore - minScore || 1;

  const fmtShort = (val) => {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const fmtFull = (val) => {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  const points = matches.map((m, i) => {
    const x = matches.length === 1
      ? width / 2
      : padSide + (i * (width - 2 * padSide)) / (matches.length - 1);
    const y = padTop + plotHeight - ((m.score - minScore) / range) * plotHeight;
    return {
      x, y, score: m.score, dateLabel: fmtShort(m.playedAt), dateFull: fmtFull(m.playedAt),
    };
  });

  const baselineY = padTop + plotHeight;
  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  // Closed shape for the fill: line points, then straight down to the baseline and back
  // to the start — a plain light wash under the line, not another data encoding.
  const areaPoints = points.length > 1
    ? `${padSide},${baselineY} ${polylinePoints} ${width - padSide},${baselineY}`
    : null;

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg width={width} height={height} role="img" aria-label="Match score history">
        {/* Baseline gives every point, including a lone one, a visible reference — no
            axis at all is exactly what made a single dot read as "broken". */}
        <line x1={padSide} y1={baselineY} x2={width - padSide} y2={baselineY} stroke={C.border} strokeWidth="1" />

        {areaPoints && <polygon points={areaPoints} fill={C.navy} opacity="0.06" />}
        {points.length > 1 && (
          <polyline points={polylinePoints} fill="none" stroke={C.navy} strokeWidth="2" />
        )}

        {points.map((p) => (
          <g key={`${p.dateLabel}-${p.score}`}>
            <line x1={p.x} y1={p.y} x2={p.x} y2={baselineY} stroke={C.border} strokeWidth="1" strokeDasharray="2,2" />
            <circle cx={p.x} cy={p.y} r={4} fill={C.green} stroke="#ffffff" strokeWidth="1.5">
              {/* Native browser tooltip on hover — exact score + full date, no library needed. */}
              <title>{`${p.score} points — ${p.dateFull}`}</title>
            </circle>
            <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize="11" fontWeight="700" fill={C.navy}>
              {p.score}
            </text>
            <text x={p.x} y={baselineY + 16} textAnchor="middle" fontSize="9.5" fill={C.muted}>
              {p.dateLabel}
            </text>
          </g>
        ))}
      </svg>
    </Box>
  );
}

export default function ScoreboardDetailModal({
  open, onClose, row, gameId, filter, from, to, rangeFrom, rangeTo,
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [highestScore, setHighestScore] = useState(null);
  const [matches, setMatches] = useState([]);
  const [limit, setLimit] = useState(DEFAULT_HISTORY_LIMIT);

  // Fresh view of whichever player was just clicked — don't carry the previous player's
  // chosen match count into a newly opened row.
  useEffect(() => {
    if (open) setLimit(DEFAULT_HISTORY_LIMIT);
  }, [open, row?.userId]);

  useEffect(() => {
    if (!open || !gameId || !row?.userId) return undefined;
    let cancelled = false;
    setLoading(true);
    getPlayerHistory(gameId, row.userId, {
      filter, from, to, limit,
    })
      .then((data) => {
        if (cancelled) return;
        setHighestScore(data?.highestScore ?? null);
        setMatches(Array.isArray(data?.matches) ? data.matches : []);
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load match history.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gameId, row?.userId, filter, from, to, limit]);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(row?.email ?? '');
      toast.success('Email copied to clipboard.');
    } catch (_) {
      toast.error('Failed to copy email.');
    }
  };

  if (!row) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          {/* Same initial-letter avatar convention as User Analytics
              (UserDrilldownModal.jsx's UserAvatar). */}
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: avatarColorFor(row.userId), color: '#ffffff', fontSize: 18, fontWeight: 700,
            }}
          >
            {initialOf(row.username || row.email)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              {row.username}
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.muted }}>
              {row.email || '—'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Separates "who this is" from "their stats" — without it the header ran straight
            into the range banner/tiles with only a margin between them, no real boundary. */}
        <Divider sx={{ mb: 2.5 }} />

        {describeScoreboardRange(filter, rangeFrom, rangeTo) && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, mb: 2.5, bgcolor: '#eff6ff', borderRadius: 2,
          }}
          >
            <EventAvailableIcon sx={{ fontSize: 16, color: C.navy }} />
            <Typography variant="body2" sx={{ fontSize: 12.5, color: C.navy, fontWeight: 600 }}>
              <LocalText>{describeScoreboardRange(filter, rangeFrom, rangeTo)}</LocalText>
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
          <Box sx={tileSx}>
            <Typography sx={{ fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <LocalText>Current Rank</LocalText>
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.navy }}>
              {row.rank}
            </Typography>
          </Box>
          <Box sx={tileSx}>
            <Typography sx={{ fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <LocalText>Highest Score</LocalText>
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.navy }}>
              {loading ? '…' : (highestScore ?? '—')}
            </Typography>
          </Box>
          <Box sx={tileSx}>
            <Typography sx={{ fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <LocalText>Total Matches</LocalText>
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.navy }}>
              {row.gamesPlayed}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b' }}>
              <LocalText>{`Match History (Last ${limit} Matches)`}</LocalText>
            </Typography>
            <FormControl size="small">
              <Select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                inputProps={{ 'aria-label': 'Number of recent matches to show' }}
                sx={{
                  fontSize: 12.5,
                  height: 30,
                  bgcolor: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
                }}
              >
                {HISTORY_LIMIT_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 12.5 }}>
                    <LocalText>{`Last ${n}`}</LocalText>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {loading ? (
            <Typography variant="body2" sx={{ color: C.muted, textAlign: 'center', py: 3 }}>
              <LocalText>Loading…</LocalText>
            </Typography>
          ) : (
            <MatchHistoryChart matches={matches} />
          )}
        </Box>

        <Typography sx={{ fontSize: 12, color: C.muted, mb: 2.5 }}>
          <LocalText>Last Session Date</LocalText>: {fmtDate(row.lastPlayedTime)}
        </Typography>

        {/* Separates the content area from the actions — a footer sitting directly under
            body text with no boundary read as unfinished/cramped. */}
        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCopyEmail}
            disabled={!row.email}
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
          >
            <LocalText>Copy Email</LocalText>
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onClose}
            sx={{ ...btnBase, ...ctaNavy }}
          >
            <LocalText>Done</LocalText>
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
